// apps/web/src/pages/vendas/fechamento/fechamento-aba.tsx — Fase 12, Plano 14 (FECH-01, FECH-02, D-20..D-23, R12-09; UI-SPEC § Aba Fechamento).
// Aba 'Fechamento' do PV (só com pv:fechar): lotes disponíveis vindos da API, seleção, quantidade total ou parcial por lote,
// barra de resumo fixa com as pendências consolidadas (simulação da API, nada é gravado) e 'Confirmar fechamento'.
// Nenhum saldo ou pendência é calculado na tela: disponível, unidade e pendências vêm da API.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileClock, RefreshCw } from 'lucide-react';
import type { FecharPvInput, LoteDisponivelFechamento, ResultadoFechamento, SimulacaoFechamento } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { apiMutate } from '../../../lib/csrf-fetch';
import { lerNumeroDigitado, paraTexto } from '../../../lib/vendas/numero';
import {
  formatarQuantidadeFechamento,
  resumoSelecao,
  totaisSelecaoPorUnidade,
  unidadePorExtenso,
  validarQuantidadeFechamento,
} from '../../../lib/vendas/fechamento';
import { ConfirmarFechamentoDialog, ResultadoFechamentoCard } from './confirmar-fechamento-dialog';
import { FechamentosAnteriores } from './fechamentos-anteriores';
import { RascunhoFiscalRevisao } from './rascunho-fiscal-revisao';

interface FechamentoAbaProps {
  readonly pedidoId: string;
  /** Versão do PV (lock otimista do fechamento); muda quando o PV é recarregado. */
  readonly versaoPv: number;
  /** NF da Omie já comprovada na projeção do PV; a aba não consulta estado fiscal por conta própria. */
  readonly numeroNfOmieComprovado: string | null;
  /** Recarrega o PV (silencioso); devolve true quando conseguiu. */
  readonly onRecarregarPv: () => Promise<boolean>;
}

interface LinhaSelecao {
  readonly marcado: boolean;
  /** Texto digitado (pt-BR: vírgula decimal; ponto só como milhar). */
  readonly texto: string;
}

const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const ATRASO_SIMULACAO_MS = 300;

function textoInicial(lote: LoteDisponivelFechamento): string {
  return paraTexto(lote.disponivel);
}

function detalhesUnicos(simulacao: SimulacaoFechamento | null): string[] {
  if (!simulacao) return [];
  return [...new Set(simulacao.pendencias.map((pendencia) => pendencia.detalhe))];
}

function PendenciaDoLote({ detalhes }: { readonly detalhes: readonly string[] }) {
  if (detalhes.length === 0) return null;
  return (
    <span
      title={detalhes.join(' · ')}
      className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
    >
      <FileClock className="size-3 shrink-0" aria-hidden="true" />
      Rascunho pendente
    </span>
  );
}

export function FechamentoAba({ pedidoId, versaoPv, numeroNfOmieComprovado, onRecarregarPv }: FechamentoAbaProps) {
  const [disponiveis, setDisponiveis] = useState<LoteDisponivelFechamento[] | null>(null);
  const [erroCarga, setErroCarga] = useState(false);
  const [linhas, setLinhas] = useState<Record<string, LinhaSelecao>>({});
  const [simulacao, setSimulacao] = useState<SimulacaoFechamento | null>(null);
  const [simulando, setSimulando] = useState(false);
  const [erroSimulacao, setErroSimulacao] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoFechamento | null>(null);
  /** Retorno cujo rascunho está aberto na revisão (substitui a lista enquanto aberto). */
  const [revisando, setRevisando] = useState<string | null>(null);
  /** Incrementa a cada gravação para a lista de fechamentos anteriores reler. */
  const [atualizacao, setAtualizacao] = useState(0);

  const carregar = useCallback(
    async (sinal: { vivo: boolean }) => {
      setErroCarga(false);
      try {
        const res = await fetch(`/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}/fechamento/disponiveis`, { credentials: 'include' });
        if (!sinal.vivo) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const lotes = (await res.json()) as LoteDisponivelFechamento[];
        if (!sinal.vivo) return;
        setDisponiveis(lotes);
        // Mantém a seleção dos lotes que continuam disponíveis; sugestão inicial = disponível (D-23).
        setLinhas((atual) => {
          const proxima: Record<string, LinhaSelecao> = {};
          for (const lote of lotes) proxima[lote.loteId] = atual[lote.loteId] ?? { marcado: false, texto: textoInicial(lote) };
          return proxima;
        });
      } catch {
        if (sinal.vivo) setErroCarga(true);
      }
    },
    [pedidoId],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar, versaoPv]);

  const selecionados = useMemo(
    () =>
      (disponiveis ?? [])
        .filter((lote) => linhas[lote.loteId]?.marcado)
        .map((lote) => {
          const texto = linhas[lote.loteId]?.texto ?? '';
          const quantidade = lerNumeroDigitado(texto);
          return { lote, quantidade, erro: validarQuantidadeFechamento(quantidade, lote.disponivel, lote.unidade, texto) };
        }),
    [disponiveis, linhas],
  );
  const todosValidos = selecionados.length > 0 && selecionados.every((item) => item.erro === null);
  const corpoLotes: FecharPvInput['lotes'] = useMemo(
    () => (todosValidos ? selecionados.map((item) => ({ loteId: item.lote.loteId, quantidade: item.quantidade })) : []),
    [selecionados, todosValidos],
  );
  const chaveSimulacao = JSON.stringify(corpoLotes);

  // Simulação das pendências (mesmo cálculo do fechar, sem gravar). Descarta respostas de seleções antigas.
  useEffect(() => {
    if (corpoLotes.length === 0) {
      setSimulacao(null);
      setSimulando(false);
      setErroSimulacao(false);
      return;
    }
    let vivo = true;
    setSimulando(true);
    setErroSimulacao(false);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const corpo: FecharPvInput = { versao: versaoPv, lotes: corpoLotes };
          const res = await apiMutate(`/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}/fechamentos/simular`, { method: 'POST', body: corpo });
          if (!vivo) return;
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const dados = (await res.json()) as SimulacaoFechamento;
          if (vivo) setSimulacao(dados);
        } catch {
          if (vivo) {
            setSimulacao(null);
            setErroSimulacao(true);
          }
        } finally {
          if (vivo) setSimulando(false);
        }
      })();
    }, ATRASO_SIMULACAO_MS);
    return () => {
      vivo = false;
      window.clearTimeout(timer);
    };
    // A chave serializa a seleção; `corpoLotes` muda de identidade a cada render do memo.
  }, [chaveSimulacao, versaoPv, pedidoId]);

  const alternar = (lote: LoteDisponivelFechamento, marcado: boolean) =>
    setLinhas((atual) => ({ ...atual, [lote.loteId]: { texto: atual[lote.loteId]?.texto ?? textoInicial(lote), marcado } }));
  const digitar = (lote: LoteDisponivelFechamento, texto: string) =>
    setLinhas((atual) => ({ ...atual, [lote.loteId]: { marcado: atual[lote.loteId]?.marcado ?? false, texto } }));

  const marcarTodos = (marcado: boolean) =>
    setLinhas((atual) => {
      const proxima: Record<string, LinhaSelecao> = {};
      for (const lote of disponiveis ?? []) proxima[lote.loteId] = { texto: atual[lote.loteId]?.texto ?? textoInicial(lote), marcado };
      return proxima;
    });

  const aoConfirmar = (dados: ResultadoFechamento) => {
    setResultado(dados);
    setAtualizacao((atual) => atual + 1);
    setLinhas({});
    setSimulacao(null);
    void carregar({ vivo: true });
    void onRecarregarPv();
  };

  const recarregarPv = async (): Promise<boolean> => {
    const ok = await onRecarregarPv();
    await carregar({ vivo: true });
    return ok;
  };

  if (revisando) {
    return (
      <RascunhoFiscalRevisao
        retornoId={revisando}
        onVoltar={() => setRevisando(null)}
        onAlterado={() => setAtualizacao((atual) => atual + 1)}
      />
    );
  }

  if (erroCarga) {
    return (
      <Card>
        <div role="alert" className="flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-rose-700">Não foi possível carregar os lotes disponíveis. Verifique a conexão e tente novamente.</p>
          <Button type="button" variant="outline" onClick={() => void carregar({ vivo: true })}>
            <RefreshCw aria-hidden="true" />
            Tentar novamente
          </Button>
        </div>
      </Card>
    );
  }

  if (disponiveis === null) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando lotes disponíveis">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const detalhesPendencias = detalhesUnicos(simulacao);
  const todosMarcados = disponiveis.length > 0 && disponiveis.every((lote) => linhas[lote.loteId]?.marcado);
  const pendenciasPorLote = (loteId: string): string[] =>
    simulacao ? [...new Set(simulacao.pendencias.filter((pendencia) => pendencia.itemRef === loteId).map((pendencia) => pendencia.detalhe))] : [];

  return (
    <div className="flex flex-col gap-4">
      {numeroNfOmieComprovado && (
        <Card>
          <p className="px-4 py-3 text-sm font-medium tabular-nums">NF Omie: {numeroNfOmieComprovado}</p>
        </Card>
      )}
      {resultado && <ResultadoFechamentoCard resultado={resultado} onRevisar={setRevisando} />}

      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold">Lotes disponíveis para faturar</h2>
        <p className="text-sm text-muted-foreground">Só entram lotes concluídos no Kanban, com quantidade aprovada no CQ e sem não conformidade aberta.</p>
      </div>

      {disponiveis.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <p className="text-sm font-medium">Nenhum lote disponível para faturar.</p>
            <p className="max-w-xl text-sm text-muted-foreground">Os lotes aparecem aqui quando concluem o último processo e são aprovados no CQ.</p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden py-0 gap-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th scope="col" className="w-10 px-3 py-2">
                  <Checkbox
                    aria-label="Selecionar todos os lotes"
                    checked={todosMarcados}
                    onCheckedChange={(valor) => marcarTodos(valor === true)}
                  />
                </th>
                <th scope="col" className={CABECALHO}>Lote</th>
                <th scope="col" className={CABECALHO}>Item</th>
                <th scope="col" className={`${CABECALHO} hidden lg:table-cell`}>NF</th>
                <th scope="col" className={`${CABECALHO} hidden text-right lg:table-cell`}>Aprovado</th>
                <th scope="col" className={`${CABECALHO} hidden text-right lg:table-cell`} title="Quantidade incluída em fechamentos do pedido (reservada para expedição)">Já fechado</th>
                <th scope="col" className={`${CABECALHO} text-right`}>Disponível</th>
                <th scope="col" className={`${CABECALHO} text-right`}>Quantidade a fechar</th>
                <th scope="col" className={`${CABECALHO} hidden lg:table-cell`}>Pendências</th>
              </tr>
            </thead>
            <tbody>
              {disponiveis.map((lote) => {
                const linha = linhas[lote.loteId];
                const marcado = linha?.marcado ?? false;
                const erro = marcado ? validarQuantidadeFechamento(lerNumeroDigitado(linha?.texto ?? ''), lote.disponivel, lote.unidade, linha?.texto ?? '') : null;
                const detalhes = marcado ? pendenciasPorLote(lote.loteId) : [];
                const aprovado = `${formatarQuantidadeFechamento(lote.aprovado, lote.unidade)} ${unidadePorExtenso(lote.aprovado, lote.unidade)}`;
                const jaFechado = `${formatarQuantidadeFechamento(lote.jaFaturado, lote.unidade)} ${unidadePorExtenso(lote.jaFaturado, lote.unidade)}`;
                const idCampo = `fechar-qtd-${lote.loteId}`;
                return (
                  <tr key={lote.loteId} className="border-t align-top">
                    <td className="px-3 py-2">
                      <Checkbox
                        aria-label={`Selecionar o lote ${lote.loteInternoPieltec}`}
                        checked={marcado}
                        onCheckedChange={(valor) => alternar(lote, valor === true)}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium tabular-nums">{lote.loteInternoPieltec}</td>
                    <td className="px-3 py-2">
                      <p>
                        <span className="tabular-nums">{lote.numeroItem}</span> · {lote.descricao}
                      </p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground lg:hidden">
                        <span>NF {lote.nfNumero ?? '—'}</span>
                        <span>Aprovado {aprovado}</span>
                        <span>Já fechado {jaFechado}</span>
                        <PendenciaDoLote detalhes={detalhes} />
                      </p>
                    </td>
                    <td className="hidden px-3 py-2 tabular-nums lg:table-cell">{lote.nfNumero ?? '—'}</td>
                    <td className="hidden px-3 py-2 text-right tabular-nums lg:table-cell">{aprovado}</td>
                    <td className="hidden px-3 py-2 text-right tabular-nums lg:table-cell">{jaFechado}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {formatarQuantidadeFechamento(lote.disponivel, lote.unidade)} {unidadePorExtenso(lote.disponivel, lote.unidade)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="ml-auto flex w-40 flex-col items-end gap-1">
                        <div className="flex items-center gap-1.5">
                          <label htmlFor={idCampo} className="sr-only">
                            Quantidade a fechar do lote {lote.loteInternoPieltec}
                          </label>
                          <Input
                            id={idCampo}
                            inputMode="decimal"
                            className="h-9 text-right tabular-nums"
                            value={linha?.texto ?? textoInicial(lote)}
                            onChange={(e) => digitar(lote, e.target.value)}
                            aria-invalid={erro !== null}
                            aria-describedby={erro ? `${idCampo}-erro` : undefined}
                          />
                          <span className="text-xs text-muted-foreground">{lote.unidade}</span>
                        </div>
                        {erro && (
                          <p id={`${idCampo}-erro`} role="alert" className="text-xs text-rose-700">
                            {erro}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="hidden px-3 py-2 lg:table-cell">
                      <PendenciaDoLote detalhes={detalhes} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {disponiveis.length > 0 && (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border bg-white px-4 py-3 shadow-md">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-sm font-medium tabular-nums">{resumoSelecao(selecionados.map((item) => ({ quantidade: item.quantidade, unidade: item.lote.unidade })).filter((item) => Number.isFinite(item.quantidade)))}</p>
            {selecionados.length > 0 && todosValidos && (
              <p className="text-[28px] font-semibold leading-tight tabular-nums">
                {totaisSelecaoPorUnidade(selecionados.map((item) => ({ quantidade: item.quantidade, unidade: item.lote.unidade })))} a faturar
              </p>
            )}
            {simulando && <p className="text-xs text-muted-foreground">Conferindo pendências do rascunho fiscal…</p>}
            {!simulando && erroSimulacao && (
              <p className="text-xs text-amber-800">Não foi possível conferir as pendências agora. O fechamento ainda pode ser confirmado.</p>
            )}
            {!simulando && detalhesPendencias.length > 0 && (
              <p className="flex items-start gap-1.5 text-sm text-amber-800">
                <FileClock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>Rascunho fiscal pendente — falta: {detalhesPendencias.join(' · ')}. Não será enviado à Omie até resolver.</span>
              </p>
            )}
          </div>
          <Button type="button" onClick={() => setConfirmando(true)} disabled={!todosValidos}>
            Confirmar fechamento
          </Button>
        </div>
      )}

      <ConfirmarFechamentoDialog
        aberto={confirmando}
        onAbertoChange={setConfirmando}
        pedidoId={pedidoId}
        versao={versaoPv}
        lotes={corpoLotes}
        pendencias={detalhesPendencias}
        onConfirmado={aoConfirmar}
        onRecarregar={recarregarPv}
      />

      <FechamentosAnteriores pedidoId={pedidoId} atualizacao={atualizacao} onAbrirRevisao={setRevisando} />
    </div>
  );
}
